import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import {find, pick, isArray, remove, findIndex, map} from 'lodash';
import {Vue} from 'vue-property-decorator';

export interface IMenuStoreState {
	menus: IMenu[];
	activeMenuId: string;
}

export interface IMenu {
	locationId: string;
	menuId: string;
	name: string;
	categories: IMenuCategory[];
	items: {
		[itemId: string]: IMenuItem;
	};
	options: {
		[optionId: string]: IMenuOption
	};
}

export interface IMenuCategory {
	categoryId: number;
	name: string;
	itemIds: string[];
}

export interface IMenuItem {
	itemId: string;
	name: string;
	description: string;
	optionIds: string[];
	price: number;
	outOfStock: boolean;
	defaultCourseId: number | null;
}

export interface IMenuOptionChoice {
	choiceId: string;
	name: string;
	description: string;
	selected: boolean;
	price: number;
}

export interface IMenuOption {
	optionId: string;
	name: string;
	choices: {
		[choiceId: string]: IMenuOptionChoice
	};
	price: number;
	exclusive: boolean;
	required: boolean;
	freeChoicesCount: number;
	priority: number;
	maxChoices: number;
	minChoices: number;
}


@Module({dynamic: true, store, name: 'menu', namespaced: true})
class Menu extends VuexModule implements IMenuStoreState {
	menus: IMenu[] = [];
	activeMenuId = '';

	public static mergeAndAdd(existingItems: any[], newItems: any[], predicateKeys: string[]) {
		newItems.forEach((newItem) => {
			const existingItemIndex = findIndex(existingItems, pick(newItem, predicateKeys));
			if(existingItemIndex > -1) {
				Vue.set(existingItems, existingItemIndex, Object.assign({}, existingItems[existingItemIndex], newItem));
			} else {
				existingItems.push(newItem);
			}
		});
	}

	get categories() {
		return (): IMenuCategory[] => {
			return this.categoriesByMenu(this.activeMenuId);
		};
	}

	get categoriesByMenu() {
		return (menuId: string): IMenuCategory[] => {
			const menu = this.menu(menuId);
			if(!menu) {
				return [];
			}
			return menu.categories;
		};
	}

	get category() {
		return (categoryName: string): IMenuCategory | null => {
			return this.categoryByMenu(this.activeMenuId, categoryName);
		};
	}

	get categoryByMenu() {
		return (menuId: string, categoryName: string): IMenuCategory | null => {
			const menu = this.menu(menuId);
			if(!menu) {
				return null;
			}
			return find(menu.categories, {name: categoryName}) || null;
		};
	}

	get menu() {
		return (menuId: string): IMenu | null => {
			return find(this.menus, {menuId: menuId}) || null;
		};
	}

	get items() {
		return (): IMenuItem[] => {
			return this.itemsByMenu(this.activeMenuId);
		};
	}

	get itemsByMenu() {
		return (menuId: string): IMenuItem[] => {
			const menu = this.menu(menuId);
			if(!menu) {
				return [];
			}
			return Object.values(menu.items);
		};
	}

	get item() {
		return (itemId: string): IMenuItem | null => {
			return this.itemByMenu(this.activeMenuId, itemId);
		};
	}

	get itemByMenu() {
		return (menuId: string, itemId: string): IMenuItem | null => {
			const menu = this.menu(menuId);
			if(!menu) {
				return null;
			}
			return menu.items[itemId] || null;
		};
	}

	get itemsForCategory() {
		return (categoryName: string): IMenuItem[] => {
			return this.itemsForCategoryByMenu(this.activeMenuId, categoryName);
		};
	}

	get itemsForCategoryByMenu() {
		return (menuId: string, categoryName: string): IMenuItem[] => {
			const menu = this.menu(menuId);
			if(!menu) {
				console.log('No Menu found');
				return [];
			}
			const category = this.categoryByMenu(menuId, categoryName);
			if(!category) {
				console.log('No Category found');
				return [];
			}
			return category.itemIds.map((itemId) => {
				return menu.items[itemId];
			});
		};
	}

	get options() {
		return (): IMenuOption[] => {
			return this.optionsByMenu(this.activeMenuId);
		};
	}

	get optionsByMenu() {
		return (menuId: string): IMenuOption[] => {
			const menu = this.menu(menuId);
			if(!menu) {
				return [];
			}
			return Object.values(menu.options);
		};
	}

	get optionsForItem() {
		return (itemId: string): IMenuOption[] => {
			return this.optionsForItemByMenu(this.activeMenuId, itemId);
		};
	}

	get optionsForItemByMenu() {
		return (menuId: string, itemId: string): IMenuOption[] => {
			const menu = this.menu(menuId);
			if(!menu) {
				return [];
			}
			const item = menu.items[itemId];
			if(!item) {
				return [];
			}
			return item.optionIds.map((optionId) => {
				return menu.options[optionId];
			});
		};
	}


	get optionByMenu() {
		return (menuId: string, optionId: string): IMenuOption | null => {
			const menu = this.menu(menuId);
			if(!menu) {
				return null;
			}
			return find(menu.options, {optionId: optionId}) || null;
		};
	}

	get choiceByOptionByMenu() {
		return (menuId: string, optionId: string, choiceId: string): IMenuOptionChoice | null => {
			const option = this.optionByMenu(menuId, optionId);
			if(!option) {
				return null;
			}
			return find(option.choices, {choiceId: choiceId}) || null;
		};
	}


	@Action
	public addOrUpdateMenu(menu: IMenu) {
		this.ADD_OR_UPDATE_MENU(menu);
	}

	@Action
	public addOrUpdateMenuAndSetActive(menu: IMenu) {
		console.log('Setting active menu: ', menu.menuId, menu);
		this.SET_ACTIVE_MENU_ID(menu.menuId);
		this.ADD_OR_UPDATE_MENU(menu);
	}

	@Action
	public setActiveMenuId(activeMenuId: string) {
		this.SET_ACTIVE_MENU_ID(activeMenuId);
	}

	@Action
	public removeMenu(menuId: string) {
		this.REMOVE_MENU(menuId);
	}

	@Action
	public clearMenus() {
		this.CLEAR_MENUS();
	}

	@Mutation
	public ADD_OR_UPDATE_MENU(menu: IMenu) {
		Menu.mergeAndAdd(this.menus, [menu], ['menuId']);
	}

	@Mutation
	public REMOVE_MENU(menuId: string) {
		const foundIndex = findIndex(this.menus, {menuId: menuId});
		if(foundIndex !== -1) {
			this.menus.splice(foundIndex, 1);
		}
	}

	@Mutation
	public SET_ACTIVE_MENU_ID(activeMenuId: string) {
		this.activeMenuId = activeMenuId;
	}

	@Mutation
	public CLEAR_MENUS() {
		this.menus = [];
	}
}

export const MenuStore = getModule(Menu);
